import { find } from 'lodash';
import getUpholsteryFromVariantSKU from 'commons/getUpholsteryFromVariantSKU';
import constructImageURLsForVariant from 'commons/constructImageURLsForVariant';
import {
  constructHeroImageURL,
  getProductPrimaryOptionId,
  getDefaultWoodstainSKU,
  getSKUForMaterialOrOptionId,
  convertSkuToImageURLValidSku,
  getDefaultOptionData,
  getValidOptionTypeIdForHeroOptionImage,
} from 'commons/productUtils';
import { getDefaultOptionImages } from 'components/consumer/product-card/imageUtils';
import {
  WOOD_PRODUCT_OPTION_ID,
  FABRIC_PRODUCT_OPTION_ID,
} from 'commons/constants';
import categoryAngles from 'commons/category-angles.json';

export const getProductImageAngle = ({ product }) => {
  if (!product) {
    return 1;
  }
  const productTypeName =
    typeof product.type === 'string' ? product.type : product.type?.name;
  const productAngle = product.defaultAngle
    ? product.defaultAngle
    : !!productTypeName && categoryAngles[productTypeName.toLowerCase()]
    ? categoryAngles[productTypeName.toLowerCase()]
    : 1;

  return productAngle;
};

export const getDefaultOptionProductImages = ({ product }) => {
  if (product.default_option_value && product.default_option_image) {
    const finalImages = getDefaultOptionImages(
      product,
      product.default_option_value,
      getProductImageAngle({ product })
    );
    return finalImages;
  }
  return [];
};

export const getImagesForClearanceVariant = ({
  variantInfo = null,
  product,
  options,
}) => {
  let categoryImages = {};
  const herocategoryImages = constructHeroImageURL(
    product,
    options,
    variantInfo.imgSku,
    getProductImageAngle({ product }),
    true
  );

  categoryImages = {
    ...herocategoryImages,
    ...constructImageURLsForVariant(
      product.id,
      variantInfo.imgSku,
      product.name,
      getUpholsteryFromVariantSKU(variantInfo.imgSku, options),
      getProductImageAngle({ product }),
      null,
      true
    ),
  };
  return categoryImages;
};

function getSkuValueFromOptionNameOrSwatchData({
  optionName = '',
  fabricSwatchData = [],
  selectedSwatchIndex,

  options,

  optionTypeId,
}) {
  if (!optionName && fabricSwatchData?.length <= selectedSwatchIndex) {
    return '';
  }
  const forMaterialValue =
    optionName || fabricSwatchData[selectedSwatchIndex]?.fabricName;

  const skuVal = getSKUForMaterialOrOptionId({
    options,
    forMaterial: forMaterialValue?.trim(),
    optionTypeId,
  });

  return skuVal || '';
}

export function getDefaultSKUandOption(product, options, optionTypeId) {
  let sku = '';
  let optionVal = '';
  if (!optionTypeId || !options?.length) {
    return { sku, optionVal };
  }

  const upholsteryOption = find(options, {
    id: optionTypeId,
  });
  const defaultWoodstainSKU = getDefaultWoodstainSKU(options);
  if (product.defaultValue) {
    sku = product.defaultValue.sku;
    optionVal = product.defaultValue.value;
  } else if (upholsteryOption) {
    sku = upholsteryOption.values[0].sku;
    optionVal = upholsteryOption.values[0].value;
  }
  return { sku: `${sku + defaultWoodstainSKU}`, optionVal };
}

export function getConstructedProductImages({
  product,
  options,
  selectedSwatchIndex,
  optionName = null,
  fabricSwatchData = null,
  defaultAngle,
  showVariation,
  displayOptionImages = false,
}) {
  if (!product || !product.slug) return null;

  if (product.slug.endsWith('-modular')) {
    return {
      transparent_config_image: product.images?.length
        ? product.images[0].url
        : product.hero_images_min?.length
        ? product.hero_images_min[0].url
        : '',
    };
  }
  const productAngle = defaultAngle || getProductImageAngle({ product });
  let optionVal = '';
  let sku = '';
  const [, optionId] = displayOptionImages
    ? getValidOptionTypeIdForHeroOptionImage(options)
    : [];
  const optionTypeId = displayOptionImages
    ? optionId
    : getProductPrimaryOptionId(options, product);
  let categoryImages = {};

  let optionSku = fabricSwatchData
    ? getSkuValueFromOptionNameOrSwatchData({
        optionName,
        fabricSwatchData,
        selectedSwatchIndex,
        optionTypeId,
        options,
      })
    : '';

  if (displayOptionImages && optionId && !fabricSwatchData?.length) {
    const defaultOption = getDefaultOptionData(options, displayOptionImages);
    optionSku = defaultOption ? { sku: defaultOption.finalSku } : '';
  }

  categoryImages = constructHeroImageURL(
    product,
    options,
    optionSku.sku,
    productAngle,
    true,
    displayOptionImages,
  );

  optionVal =
    optionName ||
    (fabricSwatchData?.length &&
    !optionName &&
    fabricSwatchData[selectedSwatchIndex]
      ? fabricSwatchData[selectedSwatchIndex].fabricName.trim()
      : optionTypeId === FABRIC_PRODUCT_OPTION_ID
      ? 'Taylor Felt Grey'
      : null);
  sku = optionSku?.sku
    ? optionSku.sku
    : fabricSwatchData?.length && optionVal
    ? getSKUForMaterialOrOptionId({
        options,
        forMaterial: optionVal,
        optionTypeId,
      }).sku
    : null;
  // Change sku to include specific variant
  if (
    showVariation &&
    !optionName &&
    !!fabricSwatchData?.length &&
    showVariation.includes(fabricSwatchData[selectedSwatchIndex].fabricSku)
  ) {
    const finalSku = convertSkuToImageURLValidSku({
      upholsterySku: fabricSwatchData[selectedSwatchIndex].fabricSku,
      fullSku: showVariation,
      options,
      optionTypeId,
    });
    sku = finalSku;
  }
  if (!sku) {
    const newDefaults = getDefaultSKUandOption(product, options, optionTypeId);
    sku = newDefaults.sku;
    if (!optionVal) optionVal = newDefaults.optionVal;
  }

  if (optionTypeId === WOOD_PRODUCT_OPTION_ID) {
    optionVal = '';
  }
  categoryImages = {
    ...categoryImages,
    ...constructImageURLsForVariant(
      product.id,
      sku,
      product.name,
      optionVal,
      productAngle,
      null,
      true
    ),
  };

  return categoryImages;
}

export const areSwatchPhotosEqual = ({ photoSet1 = [], photoSet2 = [] }) => {
  if (
    (!photoSet1 && !!photoSet2) ||
    (!!photoSet1 && !photoSet2) ||
    photoSet1?.length !== photoSet2?.length
  ) {
    return false;
  }
  if (photoSet1.length === 0 && photoSet1.length === photoSet2.length) {
    return true;
  }
  let isEqual = true;
  for (let i = 0; i < photoSet1.length; i++) {
    if (
      photoSet1[i].transparent_hero_image !==
        photoSet2[i].transparent_hero_image ||
      photoSet1[i].transparent_config_image !==
        photoSet2[i].transparent_config_image
    ) {
      isEqual = false;
      break;
    }
  }
  return isEqual;
};
